/* eslint-disable no-param-reassign */
import { createSlice } from '@reduxjs/toolkit';

import { doControlOptionSort, resolveControlWeights } from './utils';
import { getUpdatedPipedLabelsMap } from '../slice-actions/element';
import { newForm, newIdentifier } from '../../../shared/utils';
import {
  InputType,
  ControlType,
  unpipeField,
  choiceFields,
  getPipedLabel,
  assignActivePage,
  doCreateIdentifier,
  defaultIntroDetails,
  defaultTimerOptions,
  defaultClosingPages,
  generateBuilderForm,
  isSimpleTextControl,
  createControlForType,
  textFieldsWithType,
  getUpdatedBuilderLabelPiping,
  defaultSingleClosingPageDetails,
} from '../../../shared/form-lib';

export const defaultState = {
  error: '',
  isPDF: false,
  activePage: 0,
  zoomLevel: 100,
  loading: false,
  showTour: false,
  isSaving: false,
  userQuota: null,
  addRowCtrls: {},
  isNewForm: true,
  userOptions: null,
  enableTimer: false,
  isSavedForm: false,
  editingForm: false,
  showFieldIds: false,
  storedUserForms: [],
  builderForm: newForm,
  reportBuilder: false,
  showTimerModal: false,
  isTemplateForm: false,
  showLogicModal: false,
  previewViewPort: null,
  storedSourceForms: {},
  lookupDetailsList: [],
  displayIdentifiers: {},
  internalIdentifiers: {},
  tourGuideEnabled: false,
  enableESignature: false,
  showMobileSidebar: false,
  redirectToCustomise: {
    saveAsNew: false,
    status: 'no'
  },
  activeClosingPageIndex: 0,
  showCtrlRulesModal: false,
  showFormCtrlsModal: false,
  showIntroPageModal: false,
  showClearPageModal: false,
  pageDeleteRuleText: false,
  showRemovePageModal: false,
  showClosingPageModal: false,
  restoreAutosaveModal: false,
  labelPipedCtrlLabelsMap: {},
  showUpgradePlanModal: false,
  isBuilderFormUpdated: false,
  showCtrlMultipleModal: false,
  showQuotaExceededModal: false,
  showAddCalculationModal: false,
  showCtrlCalculationsModal: false,
  pageDeleteCalculationText: false,
  timerOptions: defaultTimerOptions,
  introDetails: defaultIntroDetails,
  closingPages: defaultClosingPages,
  showCustomDocumentTemplatesModal: false,

  customDocuments: {
    autorespTemplate: 'default',
    isUploadingTemplate: false,
    notifTemplate: 'default',
    templateUploadError: '',
    selectedTemplate: null,
    isFetching: false,
    processKeys: {},
    templates: [],
    columns: [],
    error: '',
    templateUploadProgress: {
      loaded: 0,
      humanSize: 0,
    },
  },
};

const builderSlice = createSlice({
  name: 'builder',
  initialState: defaultState,
  reducers: {
    doSetUpNewForm: (state, action) => {
      const { payload } = action;
      return {
        ...(payload || state),
        restoreAutosaveModal: payload ? state.restoreAutosaveModal : false,
        isSaving: false,
      };
    },

    fetchFormStart: (state) => {
      state.loading = true;
    },

    showBuilderError: (state, action) => {
      state.error = action.payload;
    },

    setUserQuota: (state, action) => {
      state.userQuota = action.payload;
    },

    fetchFormFailure: (state, action) => {
      return {
        ...state,
        ...action.payload
      };
    },

    setupSavedLocalForm: (state, action) => {
      const { form } = action.payload;
      return {
        ...state,
        ...action.payload,
        loading: false,
        isSaving: false,
        showLogicModal: false,
        showTimerModal: false,
        showProductModal: false,
        showIntroPageModal: false,
        lastSentToBE: form.modified,
        showClosingPageModal: false,
        showUpgradePlanModal: false,
        showAddCalculationModal: false,
      };
    },

    fetchFormSuccess: (state, action) => {
      const { form } = action.payload;

      const {
        builderForm,
        enableTimer,
        addRowCtrls,
        showFieldIds,
        introDetails,
        timerOptions,
        closingPages,
        enableIntroPage,
        enableESignature,
        enableClosingPage,
        oldInternalIdentifiers,
      } = generateBuilderForm(form);

      const isActivePageValid = builderForm.pages?.[state.activePage];

      return {
        ...state,
        ...action.payload,
        
        builderForm,
        addRowCtrls,
        enableTimer,
        introDetails,
        timerOptions,
        showFieldIds,
        closingPages,
        enableIntroPage,
        enableESignature,
        enableClosingPage,

        isNewForm: false,
        lastSentToBE: form.modified,
        internalIdentifiers: oldInternalIdentifiers,

        // If current activePage is not valid in new builderForm, reset to 0
        activePage: isActivePageValid ? state.activePage : 0,
      };
    },

    saveFormInfo: (state, action) => {
      const { name, value } = action.payload;
      state.builderForm[name] = value;
    },

    processNewControl: (state, action) => {
      const { control, page, clicked, result } = action.payload;
      const internalId = control.internal_id;
      const { identifiers, pages, pageIds } = state.builderForm;
      identifiers[internalId] = true;
      state.internalIdentifiers[`${control.internal_id}`] = control;
      control.is_row = false;
      const { destination } = result;
      if (clicked) {
        // Set at bottom
        pages[page][internalId] = control;
        pageIds[page].push(internalId);
      } else {
        // Initialise empty values if page is empty
        if (!pages[page]) {
          pages[page] = {};
          pageIds[page] = [];
        }
        // Set at desired index
        pages[page][internalId] = control;
        pageIds[page].splice(destination.index, 0, internalId);
      }

      const { labelPipedCtrlLabelsMap, newPages } = getUpdatedPipedLabelsMap({
        prevLabelPipedCtrlLabelsMap: state.labelPipedCtrlLabelsMap,
        pages
      });

      state.labelPipedCtrlLabelsMap = labelPipedCtrlLabelsMap;
      state.builderForm.pages = newPages;
    },

    duplicateElement: (state, action) => {
      const { control, page, index, newInnerCtrls } = action.payload;
      const internalId = control.internal_id;
      state.internalIdentifiers[`${control.internal_id}`] = control;
      const { identifiers, pages, pageIds } = state.builderForm;

      // If duplicated field is a row, update duplicated subfields in it where appropriate
      if (control.type === ControlType.ROW && newInnerCtrls.length) {
        for (let i = 0; i < control.props.controls.length; i += 1) {
          const newCtrl = newInnerCtrls[i];
          identifiers[newCtrl.internal_id] = true;
          pages[page][newCtrl.internal_id] = newCtrl;
          state.addRowCtrls[newCtrl.internal_id] = newCtrl;
          state.internalIdentifiers[`${newCtrl.internal_id}`] = newCtrl;
          pageIds[page].push(newCtrl.internal_id);
        }
      }

      identifiers[internalId] = true;
      pages[page][internalId] = control;
      pageIds[page].splice(index + 1, 0, internalId);

      const { labelPipedCtrlLabelsMap, newPages } = getUpdatedPipedLabelsMap({
        prevLabelPipedCtrlLabelsMap: state.labelPipedCtrlLabelsMap,
        isDuplicateAction: true,
        pages
      });

      state.labelPipedCtrlLabelsMap = labelPipedCtrlLabelsMap;
      state.builderForm.pages = newPages;
    },

    deleteElement: (state, action) => {
      const {
        builderForm: { pages, identifiers, pageIds, rules },
        labelPipedCtrlLabelsMap,
        internalIdentifiers,
        addRowCtrls
      } = state;
      const { control, page, rowId } = action.payload;
      const internalId = control.internal_id;

      // If deleted field is a row, delete fields in it where appropriate
      if (control.type === ControlType.ROW) {
        control.props.controls.forEach((rowCtrlInternalId) => {
          delete addRowCtrls[rowCtrlInternalId];
          delete pages[page][rowCtrlInternalId];
          delete internalIdentifiers[rowCtrlInternalId];
          delete identifiers[rowCtrlInternalId];
          pageIds[page] = pageIds[page].filter((controlId) => controlId !== rowCtrlInternalId);
        });
      }

      if (rowId) {
        const rowFields = pages[page][rowId].props.controls;
        pages[page][rowId].props.controls.splice(rowFields.indexOf(internalId), 1);
        delete addRowCtrls[internalId];
      }

      // If deleted field is inclusive in isSimpleTextControl,
      // update the renaming options for all affected file upload or signature fields
      if (control.props.required && isSimpleTextControl(control)) {
        const formFields = Object.values(internalIdentifiers);
        for (const formField of formFields) {
          const fileType = formField.type === ControlType.FILE_UPLOAD || formField.type === ControlType.SIGNATURE;
          if (fileType && formField.props.rename.internal_id === internalId) {
            for (const formPage of  pages) {
              if (
                formPage[formField.internal_id] &&
                (formPage[formField.internal_id].props.rename.enable ||
                  formPage[formField.internal_id].props.rename.internal_id)
              ) {
                formPage[formField.internal_id].props.rename.enabled = false;
                formPage[formField.internal_id].props.rename.internal_id = '';

                internalIdentifiers[formField.internal_id] = formPage[formField.internal_id];

                if (formPage[formField.internal_id].is_row) {
                  addRowCtrls[formField.internal_id] = formPage[formField.internal_id];
                }
              }
            }
          }
        }
      }

      // Remove all associated rule actions, triggers and/or entire rules
      const updatedRules = rules
        .map((rule) => ({
          ...rule,
          actions: rule.actions.filter((action) => action.id !== internalId),
          triggers: rule.triggers.filter((trigger) => trigger.id !== internalId),
        }))
        .filter((rule) => rule.actions.length > 0 && rule.triggers.length > 0);

      delete pages[page][internalId];
      delete internalIdentifiers[internalId];
      delete identifiers[internalId];
      pageIds[page] = pageIds[page].filter((controlId) => controlId !== internalId);

      state.builderForm.rules = updatedRules;
      state.rowId = null;

      const builderCtrlIds = Object.keys(internalIdentifiers || {});

      const { newLabelPipedCtrlLabelsMap, newPages } = unpipeField({
        labelPipedCtrlLabelsMap,
        editedCtrl: control,
        builderCtrlIds,
        pages
      });

      state.labelPipedCtrlLabelsMap = newLabelPipedCtrlLabelsMap;
      state.builderForm.pages = newPages;
    },

    setRowId: (state, action) => {
      state.rowId = action.payload;
    },

    processNewRowControl: (state, action) => {
      const { control, result, page, rowInternalId } = action.payload;
      state.internalIdentifiers[`${control.internal_id}`] = control;
      const { builderForm } = state;
      const { identifiers, pages, pageIds } = builderForm;
      const internalId = control.internal_id;
      control.is_row = true;
      identifiers[internalId] = true;
      pages[page][internalId] = control;
      pageIds[page].push(internalId);
      state.addRowCtrls[internalId] = control;
      if (result) {
        const { destination } = result;
        pages[page][rowInternalId].props.controls.splice(destination.index, 0, internalId);
      }

      const { labelPipedCtrlLabelsMap, newPages } = getUpdatedPipedLabelsMap({
        prevLabelPipedCtrlLabelsMap: state.labelPipedCtrlLabelsMap,
        pages
      });

      state.labelPipedCtrlLabelsMap = labelPipedCtrlLabelsMap;
      state.builderForm.pages = newPages;
    },

    processNewRowControlTemp: (state, action) => {
      const { control, rowWrapperId, page } = action.payload;
      state.internalIdentifiers[`${control.internal_id}`] = control;
      const { builderForm } = state;
      const { identifiers, pages, pageIds } = builderForm;
      const internalId = control.internal_id;
      control.is_row = true;
      identifiers[internalId] = true;
      pages[page][internalId] = control;
      state.addRowCtrls[internalId] = control;
      pageIds[page].push(internalId);
      pages[page][rowWrapperId].props.controls.push(control.internal_id);
    },

    deleteRowElement: (state, action) => {
      const { rowCtrlInternalId, rowInternalId, index, page } = action.payload;
      const {
        builderForm: { pages, identifiers },
        addRowCtrls,
      } = state;
      delete identifiers[rowCtrlInternalId];
      delete addRowCtrls[rowCtrlInternalId];
      pages[page][rowInternalId].props.controls.splice(index, 1);

      const { labelPipedCtrlLabelsMap, newPages } = getUpdatedPipedLabelsMap({
        prevLabelPipedCtrlLabelsMap: state.labelPipedCtrlLabelsMap,
        pages
      });

      state.labelPipedCtrlLabelsMap = labelPipedCtrlLabelsMap;
      state.builderForm.pages = newPages;
    },

    moveFieldIntoRow: (state, action) => {
      const { builderForm } = state;
      const { pages } = builderForm;
      const { result, rowInternalId, page } = action.payload;
      const { destination, draggableId } = result;
      const addrowCtrl = pages[page][draggableId];
      addrowCtrl.is_row = true;
      addrowCtrl.props.hidden = false;
      if (addrowCtrl.type === ControlType.EMBED_FIELD) addrowCtrl.props.height = 165;
      state.addRowCtrls[addrowCtrl.internal_id] = addrowCtrl;
      pages[page][rowInternalId].props.controls.splice(destination.index, 0, addrowCtrl.internal_id);
    },

    moveFieldInRowToPage: (state, action) => {
      const { builderForm, addRowCtrls } = state;
      const { pages, pageIds } = builderForm;
      const { result, rowInternalId, page } = action.payload;
      const { source, destination, draggableId } = result;
      const oldIndexOfRemovedFieldControl = pageIds[page].indexOf(draggableId);
      const removedFieldControl = addRowCtrls[draggableId];
      removedFieldControl.is_row = false;
      if (removedFieldControl.type === ControlType.EMBED_FIELD) removedFieldControl.props.height = 480;
      pages[page][rowInternalId].props.controls.splice(source.index, 1);
      pages[page][draggableId] = removedFieldControl;
      pageIds[page].splice(oldIndexOfRemovedFieldControl, 1);
      pageIds[page].splice(destination.index, 0, draggableId);
      delete state.addRowCtrls[draggableId];
    },

    moveFieldFromRowToRow: (state, action) => {
      const { builderForm, addRowCtrls } = state;
      const { pages } = builderForm;
      const { result, destRowInternalId, sourceRowInternalId, page } = action.payload;
      const { source, destination } = result;
      const removedFieldControl = addRowCtrls[pages[page][sourceRowInternalId].props.controls[source.index]];
      removedFieldControl.is_row = true;
      pages[page][sourceRowInternalId].props.controls.splice(source.index, 1);
      pages[page][destRowInternalId].props.controls.splice(destination.index, 0, removedFieldControl.internal_id);
    },

    doShowCtrlMultipleModal: (state, action) => {
      const { status, control, multiples } = action.payload;
      return {
        ...state,
        showCtrlMultipleModal: status,
        ctrlToBeRmvd: control,
        multiples,
      };
    },

    doShowCtrlCalculationsModal: (state, action) => {
      const { status, control } = action.payload;
      state.showCtrlCalculationsModal = status;
      state.ctrlToBeRmvd = control;
    },

    reorderPageElements: (state, action) => {
      const { activePage: page, builderForm } = state;
      const { pageIds } = builderForm;
      const { result } = action.payload;
      const { destination, source, draggableId } = result;
      pageIds[page].splice(source.index, 1);
      pageIds[page].splice(destination.index, 0, draggableId);
    },

    saveCtrl: (state, action) => {
      const { value, page, id, prop, key } = action.payload;
      const { builderForm } = state;
      const { pages } = builderForm;
      const control = pages[page][id];

      if (!control) return;
    
      if (key && prop) {
        control.props[prop][key] = value;
      } else if (prop && !key) {
        control.props[prop] = value;
    
        if (
          (prop === 'column_headers' && control.type === ControlType.MATRIX_RATING) ||
          (prop === 'options' && control.props.weights)
        ) {
          const oldWeights = control.props.weights;
          const currentOptions = control.props[prop];
          control.props.weights = resolveControlWeights(oldWeights, currentOptions);
        }
    
        if (
          (prop === 'stars_count' && control.type === ControlType.STAR_RATING) ||
          (prop === 'hearts_count' && control.type === ControlType.HEART_RATING)
        ) {
          const shapeOptionPrefix = control.type === ControlType.STAR_RATING ? 'Star' : 'Heart';
          control.props.options = Array.from({ length: value }, (_, i) => `${shapeOptionPrefix} ${i + 1}`);
          const oldWeights = control.props.weights;
          const currentOptions = control.props.options;
          control.props.weights = resolveControlWeights(oldWeights, currentOptions);
        }
    
        if (prop === 'scale_count' && control.type === ControlType.SCALE_RATING) {
          control.props.scale_options = Array.from({ length: value }, (_, i) => `Option ${i + 1}`);
          const oldWeights = control.props.weights;
          const currentScaleOptions = control.props.scale_options;
          control.props.weights = resolveControlWeights(oldWeights, currentScaleOptions);
        }
    
        if (prop === 'type' && textFieldsWithType.includes(control.type)) {
          if (value === InputType.NUMBER) {
            control.props.min_length = null;
            control.props.max_length = null;
          } else if (value === InputType.TEXT) {
            control.props.min_count = null;
            control.props.max_count = null;
            control.props.min_acceptable = null;
            control.props.max_acceptable = null;
          } else {
            control.props.min_length = null;
            control.props.max_length = null;
            control.props.min_count = null;
            control.props.max_count = null;
            control.props.min_acceptable = null;
            control.props.max_acceptable = null;
          }
        }
    
        if (value && prop === 'unstackable' && control.props.stacked && control.type === ControlType.MATRIX_RATING) {
          control.props.stacked = false;
        }
      } else {
        control.display_id = value;
      }

      const isOptionsSortable = choiceFields.includes(control.type);
      if (prop === 'sort_options' && isOptionsSortable && control.props.options) {
        const sortedProps = doControlOptionSort(control.props);
        if (sortedProps.error) return;
        control.props = sortedProps;
      }

      state.internalIdentifiers[`${id}`] = control;

      if (control.is_row) state.addRowCtrls[id] = control;

      if (['label', 'text'].includes(prop)) {
        if (control.type !== ControlType.TABLE_FIELD) {
          const builderCtrlIds = Object.keys(state.internalIdentifiers || {});

          const { newLabelPipedCtrlLabelsMap, newPages} = getUpdatedBuilderLabelPiping({
            labelPipedCtrlLabelsMap: state.labelPipedCtrlLabelsMap,
            editedCtrl: control,
            builderCtrlIds,
            pages
          });

          state.labelPipedCtrlLabelsMap = newLabelPipedCtrlLabelsMap;
          state.builderForm.pages = newPages;
        }
      }

      if (control.type === ControlType.TABLE_FIELD) {
        if (prop === 'row_headers' && control.props.show_row_headers) {
          control.props.min_rows = control.props.row_headers.length;
        }

        if (prop === 'show_row_headers') {
          control.props.min_rows = control.props.row_headers.length;
        }
      }
    },

    resetCtrl: (state, action) => {
      const { control, page } = action.payload;
      state.builderForm.pages[page][control.internal_id] = control;
      state.internalIdentifiers[control.internal_id] = control;
      if (control.is_row) state.addRowCtrls[control.internal_id] = control;
      const builderCtrlIds = Object.keys(state.internalIdentifiers || {});

      if (control.type !== ControlType.TABLE_FIELD) {
        const { pages } = state.builderForm;

        const { newLabelPipedCtrlLabelsMap, newPages } = getUpdatedBuilderLabelPiping({
          labelPipedCtrlLabelsMap: state.labelPipedCtrlLabelsMap,
          editedCtrl: control,
          builderCtrlIds,
          pages
        });

        state.labelPipedCtrlLabelsMap = newLabelPipedCtrlLabelsMap;
        state.builderForm.pages = newPages;
      } else {
        const newTableProps = control.table_props.map((tableCtrl, ctrlIndex) => {
          const colHeader = control.props.column_headers[ctrlIndex];
          const newTableCtrl = { ...tableCtrl };

          const newPipedLabel = getPipedLabel({
            labelPipedCtrlLabelsMap: state.labelPipedCtrlLabelsMap,
            label: colHeader,
            builderCtrlIds
          });

          state.labelPipedCtrlLabelsMap[control.internal_id][ctrlIndex] = newPipedLabel;
          newTableCtrl.piped_label = newPipedLabel;
          return newTableCtrl;
        });

        const newCtrl = { ...control, table_props: newTableProps };
        state.builderForm.pages[page][control.internal_id] = newCtrl;
        state.internalIdentifiers[control.internal_id] = newCtrl;
      }
    },

    unpipeNonRequiredCtrl: (state, action) => {
      const { page, id } = action.payload;
      const { builderForm } = state;
      const { pages } = builderForm;
      const control = pages[page][id];
      
      // Clean piped labels when a formerly required pipeable field is set to be non-required.
      if (!control.props.required) {
        const builderCtrlIds = Object.keys(state.internalIdentifiers || {});

        const { newLabelPipedCtrlLabelsMap, newPages } = unpipeField({
          labelPipedCtrlLabelsMap: state.labelPipedCtrlLabelsMap,
          editedCtrl: control,
          builderCtrlIds,
          pages
        });

        state.labelPipedCtrlLabelsMap = newLabelPipedCtrlLabelsMap;
        state.builderForm.pages = newPages;
      }
    },

    doChangePage: (state, action) => {
      const { index: activePage } = action.payload;
      return {
        ...state,
        showEditbar: false,
        control: null,
        activePage,
        colIndex: null,
      };
    },

    doAddPage: (state) => {
      const { builderForm } = state;
      const internalId = newIdentifier(Object.keys(builderForm.identifiers));
      const { pages, pagesName, pageIds, pagesIdentifiers, identifiers } = builderForm;
      pageIds.push([]);
      pages.push({});
      pagesIdentifiers.push(internalId);
      identifiers[internalId] = true;
      pagesName.push('default');
      state.activePage = pages.length - 1;
      state.showEditbar = false;
      state.editedId = null;
      state.colIndex = null;
    },

    doMovePageRight: (state, action) => {
      const { index } = action.payload;
      const { pages, pageIds, pagesName, pagesIdentifiers } = state.builderForm;
      const tempPageId = pagesIdentifiers[index];
      [pagesIdentifiers[index], pagesIdentifiers[index + 1]] = [pagesIdentifiers[index + 1], tempPageId];
      const temp1 = pages[index];
      const temp2 = pages[index + 1];
      [pageIds[index], pageIds[index + 1]] = [pageIds[index + 1], pageIds[index]];
      const tempPgName = pagesName[index];
      [pagesName[index], pagesName[index + 1]] = [pagesName[index + 1], tempPgName];
      pages.splice(index, 2, temp2, temp1);
      state.activePage = index + 1;
    },

    doMovePageLeft: (state, action) => {
      const { index } = action.payload;
      const { pages, pageIds, pagesName, pagesIdentifiers } = state.builderForm;
      const tempPageId = pagesIdentifiers[index];
      [pagesIdentifiers[index], pagesIdentifiers[index - 1]] = [pagesIdentifiers[index - 1], tempPageId];
      const pageTemp1 = pages[index];
      const pageTemp2 = pages[index - 1];
      [pageIds[index], pageIds[index - 1]] = [pageIds[index - 1], pageIds[index]];
      const tempPgName = pagesName[index];
      [pagesName[index], pagesName[index - 1]] = [pagesName[index - 1], tempPgName];
      pages.splice(index - 1, 2, pageTemp1, pageTemp2);
      state.activePage = index - 1;
    },
    doShowClearPageModal: (state, { payload }) => {
      state.showClearPageModal = payload;

      if (payload === false) {
        state.pageDeleteRuleText = false;
        state.pageDeleteCalculationText = false;
      }
    },
    doShowUpgradePlanModal: (state, { payload }) => {
      state.showUpgradePlanModal = payload;
    },
    doShowRemovePageModal: (state, { payload }) => {
      state.showRemovePageModal = payload;

      if (payload === false) {
        state.pageDeleteRuleText = false;
        state.pageDeleteCalculationText = false;
      }
    },

    doTogglePageDeleteRuleText: (state, action) => {
      state.pageDeleteRuleText = action.payload;
    },

    doTogglePageDeleteCalculationText: (state, action) => {
      state.pageDeleteCalculationText = action.payload;
    },
    doClearPage: (state) => {
      const {
        activePage,
        builderForm: { pages, pageIds, pagesIdentifiers, rules },
      } = state;
      const pageControlIds = pageIds[activePage];

      // Remove all associated rule actions, triggers and/or entire rules
      const updatedRules = rules
        .map((rule) => ({
          ...rule,
          actions: rule.actions.filter((action) => action.id !== pagesIdentifiers[activePage] && !pageControlIds.includes(action.id)),
          triggers: rule.triggers.filter((trigger) => !pageControlIds.includes(trigger.id)),
        }))
        .filter((rule) => rule.actions.length > 0 && rule.triggers.length > 0);

      // Clear the page and related data
      pages[activePage] = {};
      pageIds[activePage] = [];

      state.builderForm.rules = updatedRules;
      state.showClearPageModal = false;
      state.pageDeleteRuleText = false;
      state.pageDeleteCalculationText = false;
    },
    doRemovePage: (state) => {
      const {
        activePage,
        builderForm: { pages, pageIds, pagesName, pagesIdentifiers, rules },
      } = state;
      const pageControlIds = pageIds[activePage];

      // Remove all associated rule actions, triggers and/or entire rules
      const updatedRules = rules
        .map((rule) => ({
          ...rule,
          actions: rule.actions.filter((action) => action.id !== pagesIdentifiers[activePage] && !pageControlIds.includes(action.id)),
          triggers: rule.triggers.filter((trigger) => !pageControlIds.includes(trigger.id)),
        }))
        .filter((rule) => rule.actions.length > 0 && rule.triggers.length > 0);

      // Remove the page and related data
      pages.splice(activePage, 1);
      pagesIdentifiers.splice(activePage, 1);
      pageIds.splice(activePage, 1);
      pagesName.splice(activePage, 1);

      state.builderForm.rules = updatedRules;
      state.activePage = assignActivePage(pages, activePage);
      state.showRemovePageModal = false;
      state.pageDeleteRuleText = false;
      state.pageDeleteCalculationText = false;
    },
    doDuplicatePage: (state, action) => {
      const { newPageIdentifier, newPageIds, newPageControls, newAddRowControls } = action.payload;
      const {
        builderForm: { identifiers, pages, pageIds, pagesIdentifiers, pagesName },
        internalIdentifiers,
        addRowCtrls,
        activePage,
      } = state;

      identifiers[newPageIdentifier] = true;
      pageIds.splice(activePage + 1, 0, newPageIds);
      pages.splice(activePage + 1, 0, newPageControls);
      pagesIdentifiers.splice(activePage + 1, 0, newPageIdentifier);
      pagesName.splice(activePage + 1, 0, 'default');

      // Update identifiers and internalIdentifiers for new controls
      for (const controlId of Object.keys(newPageControls)) {
        identifiers[controlId] = true;
        internalIdentifiers[controlId] = newPageControls[controlId];
      }

      // Update addRowCtrls for new row controls
      for (const addRowControlId of Object.keys(newAddRowControls)) {
        addRowCtrls[addRowControlId] = newAddRowControls[addRowControlId];
      }

      const { labelPipedCtrlLabelsMap, newPages } = getUpdatedPipedLabelsMap({
        prevLabelPipedCtrlLabelsMap: state.labelPipedCtrlLabelsMap,
        isDuplicateAction: true,
        pages
      });

      state.labelPipedCtrlLabelsMap = labelPipedCtrlLabelsMap;
      state.builderForm.pages = newPages;
    },
    doChangePageName: (state, action) => {
      const { value: name, index } = action.payload;
      const { pagesName } = state.builderForm;
      pagesName[index] = name;
    },

    moveFieldUp: (state, action) => {
      const { index: id } = action.payload;
      const { pageIds } = state.builderForm;
      const { activePage } = state;
      const activePageIds = pageIds[activePage];
      const temp = activePageIds[id];
      [activePageIds[id], activePageIds[id - 1]] = [activePageIds[id - 1], temp];
    },

    moveFieldDown: (state, action) => {
      const { index: id } = action.payload;
      const { pageIds } = state.builderForm;
      const { activePage } = state;
      const activePageIds = pageIds[activePage];
      const temp = activePageIds[id];
      [activePageIds[id], activePageIds[id + 1]] = [activePageIds[id + 1], temp];
    },

    moveFieldToDiffPage: (state, action) => {
      const { fieldIndex, pageIndex, fieldId } = action.payload;
      const { activePage } = state;
      const { pageIds, pages } = state.builderForm;
      const fieldData = pages[activePage][fieldId];
      pageIds[activePage].splice(fieldIndex, 1);
      pages[pageIndex][fieldId] = fieldData;
      pageIds[pageIndex].push(fieldId);
      delete pages[activePage][fieldId];

      // If moved field is a row,
      // Move its subfields to new page
      if (fieldData.type === ControlType.ROW && fieldData.props.controls.length) {
        for (let i = 0; i < fieldData.props.controls.length; i += 1) {
          const rowInnerFieldId = fieldData.props.controls[i];
          const rowInnerFieldData = pages[activePage][rowInnerFieldId];
          const rowInnerFieldIndex = pageIds[activePage].indexOf(rowInnerFieldId);
          pageIds[activePage].splice(rowInnerFieldIndex, 1);
          pages[pageIndex][rowInnerFieldId] = rowInnerFieldData;
          pageIds[pageIndex].push(rowInnerFieldId);
          delete pages[activePage][rowInnerFieldId];
        }
      }
    },

    saveQuickEdit: (state, action) => {
      const { page, activeEditField, fieldProp, value, key } = action.payload;
      const { pages } = state.builderForm;
    
      if (!pages[page] || !pages[page][activeEditField]) {
        console.error(`Invalid page or activeEditField: Page - ${page}, Field - ${activeEditField}`);
        return;
      }
    
      const control = pages[page][activeEditField];
      const controlProps = control.props;
    
      if (key && fieldProp) controlProps[fieldProp][key] = value;
      else if (fieldProp) controlProps[fieldProp] = value;
      state.internalIdentifiers[activeEditField] = control;
    
      if (['label', 'text'].includes(fieldProp)) {
        if (control.type !== ControlType.TABLE_FIELD) {
          const builderCtrlIds = Object.keys(state.internalIdentifiers || {});

          const { newLabelPipedCtrlLabelsMap, newPages } = getUpdatedBuilderLabelPiping({
            labelPipedCtrlLabelsMap: state.labelPipedCtrlLabelsMap,
            editedCtrl: control,
            builderCtrlIds,
            pages
          });

          state.labelPipedCtrlLabelsMap = newLabelPipedCtrlLabelsMap;
          state.builderForm.pages = newPages;
        }
      }
    },
    
    doAddTableCol: (state, action) => {
      const { pgIndex, tableId } = action.payload;
      const { pages } = state.builderForm;
      const table = pages[pgIndex][tableId];
      const columnType = 'text_input';
      table.table_props.push(createControlForType(columnType));
      table.props.column_headers.push('Column');
      table.props.column_types.push(columnType);
      table.props.column_widths.push(0);
      table.props.columns += 1;

      const { labelPipedCtrlLabelsMap, newPages } = getUpdatedPipedLabelsMap({
        prevLabelPipedCtrlLabelsMap: state.labelPipedCtrlLabelsMap,
        pages
      });

      state.labelPipedCtrlLabelsMap = labelPipedCtrlLabelsMap;
      state.builderForm.pages = newPages;
    },

    doDeleteTableCol: (state, action) => {
      const { pgIndex, tableId, colIndex } = action.payload;
      const { pages } = state.builderForm;
      const table = pages[pgIndex][tableId];
      table.table_props.splice(colIndex, 1);
      table.props.column_headers.splice(colIndex, 1);
      table.props.column_types.splice(colIndex, 1);
      table.props.column_widths.splice(colIndex, 1);
      table.props.columns -= 1;

      const { labelPipedCtrlLabelsMap, newPages } = getUpdatedPipedLabelsMap({
        prevLabelPipedCtrlLabelsMap: state.labelPipedCtrlLabelsMap,
        pages
      });

      state.labelPipedCtrlLabelsMap = labelPipedCtrlLabelsMap;
      state.builderForm.pages = newPages;
    },

    editColProp: (state, action) => {
      const { colIndex, id, page, value, prop, isTableProp } = action.payload;
      const { pages } = state.builderForm;
      if (isTableProp) {
        pages[page][id].table_props[colIndex][prop] = value;

        if (prop === 'options' && pages[page][id].table_props[colIndex].weights) {
          const oldWeights = pages[page][id].table_props[colIndex].weights;
          const currentOptions = pages[page][id].table_props[colIndex].options;
          pages[page][id].table_props[colIndex].weights = resolveControlWeights(oldWeights, currentOptions);
        }

        const columnCtrlType = pages[page][id].props.column_types[colIndex];

        if (
          (prop === 'stars_count' && columnCtrlType === ControlType.STAR_RATING) ||
          (prop === 'hearts_count' && columnCtrlType === ControlType.HEART_RATING)
        ) {
          const shapeOptionPrefix = columnCtrlType === ControlType.STAR_RATING ? 'Star' : 'Heart';
          pages[page][id].table_props[colIndex].options = Array.from({ length: value }, (_, i) => `${shapeOptionPrefix} ${i + 1}`);
          const oldWeights = pages[page][id].table_props[colIndex].weights;
          const currentOptions = pages[page][id].table_props[colIndex].options;
          pages[page][id].table_props[colIndex].weights = resolveControlWeights(oldWeights, currentOptions);
        }

        if (
          (prop === 'scale_count' && columnCtrlType === ControlType.SCALE_RATING)
        ) {
          pages[page][id].table_props[colIndex].scale_options = Array.from({ length: value }, (_, i) => `Option ${i + 1}`);
          const oldWeights = pages[page][id].table_props[colIndex].weights;
          const currentScaleOptions = pages[page][id].table_props[colIndex].scale_options;
          pages[page][id].table_props[colIndex].weights = resolveControlWeights(oldWeights, currentScaleOptions);
        }

        if (prop === 'sort_options') {
          const isOptionsSortable = choiceFields.includes(columnCtrlType);
          const columnCtrlProps = pages[page][id].table_props[colIndex];

          if (isOptionsSortable && columnCtrlProps.options) {
            const sortedProps = doControlOptionSort(columnCtrlProps);
            if (sortedProps.error) return;
            pages[page][id].table_props[colIndex] = sortedProps;
          }
        }
      } else {
        if (prop === 'column_types') pages[page][id].table_props[colIndex] = createControlForType(value);
        if (prop === 'column_headers') {
          const builderCtrlIds = Object.keys(state.internalIdentifiers || {});

          const newPipedLabel = getPipedLabel({
            labelPipedCtrlLabelsMap: state.labelPipedCtrlLabelsMap,
            builderCtrlIds,
            label: value
          });

          pages[page][id].table_props[colIndex].piped_label = newPipedLabel;
          state.labelPipedCtrlLabelsMap[id][colIndex] = newPipedLabel;
        }

        pages[page][id].props[prop][colIndex] = value;
      }
    },

    doSaveTableColHeader: (state, action) => {
      const { pgIndex, tableId, colIndex, value } = action.payload;
      const { pages } = state.builderForm;
      const table = pages[pgIndex][tableId];
      if (colIndex >= 0) {
        const builderCtrlIds = Object.keys(state.internalIdentifiers || {});
        table.props.column_headers[colIndex] = value;

        const newPipedLabel = getPipedLabel({
          labelPipedCtrlLabelsMap: state.labelPipedCtrlLabelsMap,
          builderCtrlIds,
          label: value
        });
        
        state.labelPipedCtrlLabelsMap[tableId][colIndex] = newPipedLabel;
        table.table_props[colIndex].piped_label = newPipedLabel;
      } else {
        table.props.row_headers_column_header = value;
      }
      table._validationError = null;
    },

    doZoomForm: (state, action) => {
      const { payload } = action;
      const { zoomLevel } = state;

      if (payload === '+') state.zoomLevel = zoomLevel + 1;
      else if (payload === '-') state.zoomLevel = zoomLevel - 1;
    },
    doShowCalculationModal: (state, action) => {
      return {
        ...state,
        ...action.payload,
      };
    },

    doSaveCalculation: (state, action) => {
      const { calculations } = state.builderForm;
      calculations.push(action.payload);
    },

    doEditCalculation: (state, action) => {
      const { idx, calculation } = action.payload;
      const { calculations } = state.builderForm;
      calculations.splice(idx, 1, calculation);
    },

    doRemoveCalculation: (state, action) => {
      const { payload } = action;
      const { calculations } = state.builderForm;
      calculations.splice(payload, 1);
    },

    createResultField: (state, action) => {
      const { control, page } = action.payload;
      const { pageIds, identifiers, pages } = state.builderForm;
      state.internalIdentifiers[`${control.internal_id}`] = control;
      const internalId = control.internal_id;
      identifiers[internalId] = true;
      pages[page][internalId] = control;
      pageIds[page].push(internalId);
    },

    showCtrlRulesModal: (state, action) => {
      const { status, control } = action.payload;
      state.showCtrlRulesModal = status;
      state.ctrlToBeRmvd = control;
    },

    removeCtrlRules: (state, action) => {
      const { rules } = state.builderForm;
      const { payload } = action;
      for (let r = 0; r < payload.length; r += 1) {
        rules.splice(payload[r], 1);
      }
      state.showCtrlRulesModal = false;
    },

    removeCtrlCalculations: (state, action) => {
      const { calculations } = state.builderForm;
      const { payload } = action;
      for (let r = 0; r < payload.length; r += 1) {
        calculations.splice(payload[r], 1);
      }
      state.showCtrlCalculationsModal = false;
    },
    doShowLogicModal: (state, action) => {
      const { payload } = action;
      state.showLogicModal = payload;
      if (payload === false) state.formRuleIdx = null;
    },
    doSetFormRuleIdx: (state, { payload }) => {
      state.formRuleIdx = payload;
    },

    doShowTimerModal: (state, action) => {
      const { payload } = action;
      state.showTimerModal = payload;
    },

    doEnableTimer: (state, action) => {
      const { payload } = action;
      state.enableTimer = payload;
    },

    doEnableIntroPage: (state, action) => {
      const { payload } = action;
      state.enableIntroPage = payload;
    },

    doEnableClosingPage: (state, action) => {
      const { payload } = action;
      state.enableClosingPage = payload;
    },

    showFieldIds: (state) => {
      state.showFieldIds = !state.showFieldIds;
    },

    saveRule: (state, action) => {
      const { payload } = action;
      const { rules } = state.builderForm;
      rules.unshift(payload);
    },

    editRule: (state, action) => {
      const { idx, rule } = action.payload;
      const { rules } = state.builderForm;
      rules.splice(idx, 1, rule);
    },

    deleteRule: (state, action) => {
      const { payload } = action;
      const { rules } = state.builderForm;
      rules.splice(payload, 1);
    },

    removeRules: (state) => {
      const { rules } = state.builderForm;
      rules.length = 0;
    },

    replaceAllRules: (state, action) => {
      state.builderForm.rules = action.payload;
    },

    setPreviewPage: (state, action) => {
      state.inPreviewPage = action.payload;
    },

    doSetPreviewViewPort: (state, action) => {
      state.previewViewPort = action.payload;
    },

    showReportFormInBuilder: (state, action) => {
      state.reportBuilder = action.payload;
    },

    doShowFormControlsModal: (state, action) => {
      const { payload } = action;
      state.showFormCtrlsModal = payload;
    },

    setCustomDocumentTemplates: (state, { payload }) => {
      state.customDocuments = { ...state.customDocuments, ...payload }
    },

    updateCustomDocumentTemplates: (state, { payload }) => {
      const { values: { deletedTemplateKey = null, newTemplate = null } = {} } = payload;

      if (deletedTemplateKey) {
        state.customDocuments.templates = state.customDocuments.templates?.filter(
          (template) => template.template_key !== deletedTemplateKey
        );
      }

      if (newTemplate) {
        state.customDocuments.templates = [newTemplate, ...(state.customDocuments.templates || [])];
      }
    },

    updateCustomDocumentsProcessKeys: (state, { payload }) => {
      const {
        values: { type, deletedTemplateKey },
      } = payload;

      if (type === 'add') {
        state.customDocuments.processKeys[deletedTemplateKey] = true;
        state.customDocuments.selectedTemplate = null;
      } else if (type === 'delete') {
        delete state.customDocuments.processKeys[deletedTemplateKey];
      }
    },

    doShowCustomDocumentTemplatesModal: (state,  { payload }) => {
      state.showCustomDocumentTemplatesModal = payload;
    },

    doShowQuotaExceededModal: (state, action) => {
      const { payload } = action;
      state.showQuotaExceededModal = payload;
    },

    clearForm: (state) => {
      return {
        ...state,
        builderForm: {
          ...newForm,
          name: state.builderForm.name,
        },
        activePage: 0,
        showFormCtrlsModal: false,
        addRowCtrls: {},
        internalIdentifiers: {},
      };
    },

    showFieldError: (state, action) => {
      const { ctrl, index } = action.payload;
      const {
        builderForm: { pages },
        addRowCtrls,
      } = state;
      pages[index][ctrl.internal_id] = ctrl;
      if (ctrl.is_row && ctrl.internal_id in addRowCtrls) {
        addRowCtrls[ctrl.internal_id] = ctrl;
      }
    },

    clearFieldError: (state, action) => {
      const { editedId } = action.payload;
      const {
        builderForm: { pages },
        addRowCtrls,
      } = state;
      for (const page of pages) {
        const ctrl = page[editedId];
        if (ctrl) ctrl._validationError = null;
      }

      if (editedId in addRowCtrls) {
        addRowCtrls[editedId]._validationError = null;
      }
    },

    savingForm: (state, action) => {
      state.isSaving = action.payload;
    },

    doShowMobileSidebar: (state) => {
      state.showMobileSidebar = true;
    },

    doHideMobileSidebar: (state) => {
      state.showMobileSidebar = false;
    },

    toggleTourGuide: (state, action) => {
      state.tourGuideEnabled = action.payload;
    },

    doShowTour: (state, action) => {
      state.showTour = action.payload;
    },

    fetchPDFSuccess: (state, action) => {
      const { builderForm, oldInternalIdentifiers, addRowCtrls } = generateBuilderForm(action.payload);
      return {
        ...state,
        builderForm,
        internalIdentifiers: oldInternalIdentifiers,
        isPdf: true,
        addRowCtrls,
      };
    },

    updateAutoSave: (state, action) => {
      state.restoreAutosaveModal = action.payload.status;
      state.autoSavedResponse = action.payload.autoSavedResponse;
    },

    resetState: (state) => {},

    doSaveUserOptions: (state, { payload }) => {
      state.userOptions = payload;
    },
    doShowIntroPageModal: (state, { payload }) => {
      state.showIntroPageModal = payload;
    },
    doShowClosingPageModal: (state, { payload }) => {
      state.showClosingPageModal = payload;
    },
    doSwitchClosingPageIndex: (state, { payload }) => {
      state.activeClosingPageIndex = payload;
    },
    doReorderClosingPages: (state, { payload }) => {
      state.closingPages = payload;
    },
    doCreateClosingPage: (state) => {
      const { identifiers } = state.builderForm;
      const pageId = doCreateIdentifier(identifiers);
      const newClosingPage = { ...defaultSingleClosingPageDetails, id: pageId, title: "New Page" };
      const newClosingPages = [...state.closingPages, newClosingPage];
      state.closingPages = newClosingPages;
      state.activeClosingPageIndex = newClosingPages.length - 1;
    },
    doDeleteClosingPage: (state, { payload }) => {
      const {
        closingPages,
        builderForm: { rules },
        activeClosingPageIndex,
      } = state;

      const pageIndex = payload;
      // const pageId = closingPages[activeClosingPageIndex].id;
      const pageId = closingPages[pageIndex].id;

      // Filter out rules that have no actions related to the page and rules with empty actions
      const updatedRules = rules.filter((rule) => {
        rule.actions = rule.actions.filter((action) => action.id !== pageId);
        return rule.actions.length > 0;
      });

      // delete page
      // const newClosingPages = closingPages.filter((_, index) => index !== activeClosingPageIndex);
      const newClosingPages = closingPages.filter((_, index) => index !== pageIndex);
      state.closingPages = newClosingPages;
      state.builderForm.rules = updatedRules;
      if (pageIndex <= activeClosingPageIndex) state.activeClosingPageIndex = activeClosingPageIndex - 1;
      // if (pageIndex === activeClosingPageIndex) state.activeClosingPageIndex = activeClosingPageIndex - 1;
    },
    doUpdateIntroPageProp: (state, { payload }) => {
      const { newValue, property } = payload;
      state.introDetails[property] = newValue;
    },
    doUpdateClosingPageProp: (state, { payload }) => {
      const { newValue, property } = payload;
      const { closingPages, activeClosingPageIndex } = state;
      closingPages[activeClosingPageIndex][property] = newValue;
    },
    doResetIntroPage: (state) => {
      state.introDetails = defaultIntroDetails;
    },
    doResetClosingPage: (state) => {
      const { closingPages, activeClosingPageIndex } = state;
      const currentPage = closingPages[activeClosingPageIndex];
      const cleanClosingPage = {
        ...defaultSingleClosingPageDetails,
        title: currentPage.title || 'Untitled',
        id: currentPage.id,
      };
      closingPages[activeClosingPageIndex] = cleanClosingPage;
    },
    saveClosingPageDetails: (state, { payload }) => {
      const { pageIndex, closingPageInfo } = payload;
      state.closingPages[pageIndex] = closingPageInfo;
    },
    saveTimerOptions: (state, { payload }) => {
      state.timerOptions = payload;
      state.showTimerModal = false;
    },
    doStoreUserForms: (state, { payload }) => {
      state.storedUserForms = payload;
    },
    doStoreSourceForms: (state, { payload }) => {
      const { controlId, sourceId, options } = payload;
      state.storedSourceForms[controlId] = { sourceId, options };
    },
    disablePaymentUserSpecify: (state, action) => {
      const paymentCtrls = action.payload;
      const { calculations, pages } = state.builderForm;
    
      // Create a Set of calculation result field IDs
      const calculationResultIds = new Set(calculations.map(({ result }) => result));
    
      for (const { internal_id } of paymentCtrls) {
        if (!calculationResultIds.has(internal_id)) continue;
    
        // Deactivate user_specify for payment fields set as calculation result
        for (const page of pages) {
          const control = page[internal_id];
          if (control?.props?.user_specify) {
            control.props.user_specify = false;
          }
        }
      }
    },
    setRedirectToCustomise: (state, action) => {
      state.redirectToCustomise = {
        ...state.redirectToCustomise,
        ...action.payload
      };
    },
    setIsBuilderFormUpdated: (state, action) => {
      state.isBuilderFormUpdated = action.payload;
    },
    createPipedLabelMap: (state, { payload }) => {
      // This is a backward compatibility measure to attach piped label to all controls to 
      // in order to maintain uniformity when they get piped into another field.
      const { labelPipedCtrlLabelsMap, newPages } = getUpdatedPipedLabelsMap({
        prevLabelPipedCtrlLabelsMap: state.labelPipedCtrlLabelsMap,
        pages: state.builderForm.pages,
        isPageLoadAction: true
      });

      state.labelPipedCtrlLabelsMap = labelPipedCtrlLabelsMap;
      state.builderForm.pages = newPages;
    },
    indicateFormDeactivation: (state, action) => {
      state.isDeactivatedForm =  action.payload;
    },
  },
});

const { reducer } = builderSlice;
export default reducer;

export const {
  setRowId,
  saveRule,
  editRule,
  saveCtrl,
  resetCtrl,
  doAddPage,
  clearForm,
  deleteRule,
  resetState,
  doZoomForm,
  doShowTour,
  savingForm,
  removeRules,
  editColProp,
  moveFieldUp,
  doClearPage,
  setUserQuota,
  showFieldIds,
  doChangePage,
  doRemovePage,
  saveFormInfo,
  saveQuickEdit,
  doEnableTimer,
  doAddTableCol,
  moveFieldDown,
  deleteElement,
  fetchFormStart,
  doSetUpNewForm,
  doMovePageLeft,
  setPreviewPage,
  showFieldError,
  updateAutoSave,
  clearFieldError,
  doMovePageRight,
  doDuplicatePage,
  removeCtrlRules,
  fetchPDFSuccess,
  toggleTourGuide,
  replaceAllRules,
  doShowLogicModal,
  doSetFormRuleIdx,
  doDeleteTableCol,
  saveTimerOptions,
  doStoreUserForms,
  deleteRowElement,
  moveFieldIntoRow,
  showBuilderError,
  duplicateElement,
  fetchFormFailure,
  fetchFormSuccess,
  doChangePageName,
  doShowTimerModal,
  doResetIntroPage,
  createResultField,
  doSaveCalculation,
  doEditCalculation,
  doSaveUserOptions,
  doEnableIntroPage,
  processNewControl,
  showCtrlRulesModal,
  doStoreSourceForms,
  doResetClosingPage,
  createPipedLabelMap,
  doCreateClosingPage,
  doDeleteClosingPage,
  doRemoveCalculation,
  setupSavedLocalForm,
  doHideMobileSidebar,
  doShowMobileSidebar,
  reorderPageElements,
  moveFieldToDiffPage,
  doEnableClosingPage,
  doSaveTableColHeader,
  processNewRowControl,
  saveIntroPageDetails,
  doShowIntroPageModal,
  moveFieldInRowToPage,
  doSetPreviewViewPort,
  doShowClearPageModal,
  doReorderClosingPages,
  doUpdateIntroPageProp,
  doShowRemovePageModal,
  moveFieldFromRowToRow,
  unpipeNonRequiredCtrl,
  doShowCalculationModal,
  removeCtrlCalculations,
  saveClosingPageDetails,
  doShowClosingPageModal,
  doShowUpgradePlanModal,
  setRedirectToCustomise,
  doShowCtrlMultipleModal,
  showReportFormInBuilder,
  doShowFormControlsModal,
  doUpdateClosingPageProp,
  setIsBuilderFormUpdated,
  doShowQuotaExceededModal,
  doSwitchClosingPageIndex,
  indicateFormDeactivation,
  processNewRowControlTemp,
  disablePaymentUserSpecify,
  doTogglePageDeleteRuleText,
  setCustomDocumentTemplates,
  doShowCtrlCalculationsModal,
  updateCustomDocumentTemplates,
  updateCustomDocumentsProcessKeys,
  doTogglePageDeleteCalculationText,
  doShowCustomDocumentTemplatesModal,
} = builderSlice.actions;
